import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

// Module-level set of session_ids already in-flight or consumed.
// A useRef flag would reset on StrictMode's synthetic unmount/remount, which causes the
// session_id to be sent twice — the second call 401s because Emergent OAuth session_ids are
// single-use, and the user silently bounces back to /login. Hoisting the guard to module
// scope makes it survive remounts within the same page lifetime.
const _processedSessionIds = new Set();

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    if (!sessionIdMatch) {
      navigate('/login');
      return;
    }
    const sessionId = sessionIdMatch[1];
    if (_processedSessionIds.has(sessionId)) return; // already handled (StrictMode remount)
    _processedSessionIds.add(sessionId);

    const processAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (response.ok) {
          const userData = await response.json();
          // Mobile-Safari / ITP fallback: also keep the token in localStorage.
          if (userData.session_token) {
            localStorage.setItem('session_token', userData.session_token);
          }
          setUser(userData);
          // Clear the fragment so a refresh doesn't re-consume the used session_id.
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          const body = await response.json().catch(() => ({}));
          const reason = body?.detail || `Server rejected the sign-in (HTTP ${response.status}).`;
          setError(typeof reason === 'string' ? reason : 'Server rejected the sign-in.');
        }
      } catch (e) {
        console.error('Auth callback error:', e);
        setError('Network error while signing you in. Please check your connection and try again.');
      }
    };
    processAuth();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="auth-callback-error">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-12 h-12 mx-auto rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold">Sign-in couldn't complete</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground">
            This usually happens when the sign-in link has already been used or has expired.
            Clicking "Try again" will take you back to the login page.
          </p>
          <Button onClick={() => { window.location.hash = ''; navigate('/login'); }} data-testid="auth-retry-btn">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
